import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./Assistant.css";

/* ------------------ Utils ------------------ */
function uid(): string {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch {}
  try {
    const b = typeof crypto !== "undefined" && crypto.getRandomValues ? crypto.getRandomValues(new Uint8Array(16)) : null;
    if (b) {
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b, n => n.toString(16)).map(s => s.padStart(2,"0")).join("");
      return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
    }
  } catch {}
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,10)}`;
}
type Msg = { id: string; from: "user" | "bot" | "system"; text: string; ts?: number; };

function getSessionId(): string {
  if (typeof window === "undefined") return "server";
  const k = "nextudio_sid";
  let v = localStorage.getItem(k);
  if (!v) { v = uid(); localStorage.setItem(k, v); }
  return v;
}
function isIOS(): boolean {
  try { return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1); }
  catch { return false; }
}
function escapeHtml(s: string) {
  return s.replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;").replaceAll('"',"&quot;");
}

/* ------------------ Config ------------------ */
const API = "https://hassansalemeh.app.n8n.cloud/webhook/52f86ca0-b9b4-4ba6-b5e9-45679c83733e";
const CHAT_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours
const MAX_STORED = 200;

/* ------------------ Component ------------------ */
const AssistantPage: React.FC = () => {
  // State
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const cardRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hydratedRef = useRef(false);     // prevent writing before restore
  const messagesRef = useRef<Msg[]>([]);
  const inputRef = useRef<string>("");

  // NEW: cancel in-flight requests + ignore state after unmount
  const abortRef = useRef<AbortController | null>(null);
  const aliveRef  = useRef(true);

  // Keep live refs in sync
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { inputRef.current = input; }, [input]);

  // Session + normalized key (stable across /path and /path/)
  const sid = useMemo(getSessionId, []);
  const CHAT_STORE_KEY = useMemo(() => {
    const href = typeof window !== "undefined" ? window.location.href : "root";
    const u = new URL(href);
    const normalizedPath = u.pathname.replace(/\/+$/, "") || "/";
    return `nextudio_chat_${sid}_${normalizedPath}`;
  }, [sid]);

  /* ---------- Scroll helper ---------- */
  function scrollToBottom(opts?: { force?: boolean }) {
    const chat = chatRef.current || (document.querySelector(".chat-window") as HTMLElement | null);
    if (!chat) return;
    const kbOpen = document.body.classList.contains("kb-open");
    const nearBottom = chat.scrollHeight - chat.scrollTop - chat.clientHeight < 24;
    if (!opts?.force && !nearBottom) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        chat.scrollTop = chat.scrollHeight; // instant to avoid footer slipping
        if (!isIOS() || !kbOpen) {
          chat.scrollTo({ top: chat.scrollHeight, behavior: "smooth" });
        }
      });
    });
  }

  /* ---------- Persistence ---------- */
  const saveSnapshot = useCallback(() => {
    if (!hydratedRef.current) return;
    try {
      const payload = { ts: Date.now(), input: inputRef.current, messages: messagesRef.current.slice(-MAX_STORED) };
      localStorage.setItem(CHAT_STORE_KEY, JSON.stringify(payload));
    } catch {}
  }, [CHAT_STORE_KEY]);

  const restoreFromStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem(CHAT_STORE_KEY);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (Date.now() - (data.ts || 0) < CHAT_TTL_MS) {
        if (Array.isArray(data.messages)) setMessages(data.messages);
        if (typeof data.input === "string") setInput(data.input);
        requestAnimationFrame(() => scrollToBottom({ force: true }));
        return true;
      } else {
        localStorage.removeItem(CHAT_STORE_KEY);
      }
    } catch {}
    return false;
  }, [CHAT_STORE_KEY]);

  // Restore on mount
  useEffect(() => {
    restoreFromStorage();
    hydratedRef.current = true;
  }, [restoreFromStorage]);

  // Save on every change (after hydration)
  useEffect(() => { saveSnapshot(); }, [messages, input, saveSnapshot]);

  // Save on navigation away, and restore on bfcache return
  useEffect(() => {
    const onPageHide = () => saveSnapshot();
    const onBeforeUnload = () => saveSnapshot();
    const onVisibility = () => { if (document.visibilityState === "hidden") saveSnapshot(); };
    const onPageShow = (e: any) => {
      if (e?.persisted || messagesRef.current.length === 0) restoreFromStorage();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [saveSnapshot, restoreFromStorage]);

  /* ---------- UI/behavior effects ---------- */
  // Auto-scroll when messages or typing change
  useEffect(() => { scrollToBottom(); }, [messages, loading]);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current; if (!el) return;
    el.style.height = "0px";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  }, [input]);

  // Mark page as assistant + iOS flag
  useEffect(() => {
    const cls = "assistant-open";
    document.documentElement.classList.add(cls);
    document.body.classList.add(cls);
    if (isIOS()) document.body.classList.add("ios");
    try { window.scrollTo({ top: 0 }); } catch {}
    return () => {
      document.documentElement.classList.remove(cls);
      document.body.classList.remove(cls);
      document.body.classList.remove("ios");
    };
  }, []);

  // OPTIONAL: silence dev “Script error.” overlay & abort rejections
  useEffect(() => {
    const onError = (e: ErrorEvent) => { if (e.message === "Script error.") e.preventDefault(); };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      if (r && (r.name === "AbortError" || /AbortError|The operation was aborted/i.test(String(r)))) e.preventDefault();
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  // Keyboard-aware viewport (vvh/kb vars)
  useEffect(() => {
    const vv = (window as any).visualViewport;
    const root = document.documentElement;
    const update = () => {
      const h = vv?.height ?? window.innerHeight;
      const top = vv?.offsetTop ?? 0;
      const kb  = Math.max(0, window.innerHeight - h - top);
      root.style.setProperty("--vvh", `${h}px`);
      root.style.setProperty("--kb",  `${kb}px`);
      const ae = document.activeElement as HTMLElement | null;
      const activeIsText = !!ae && (ae.tagName === "TEXTAREA" || ae.tagName === "INPUT");
      document.body.classList.toggle("kb-open", kb > 10 || activeIsText);

      const card = cardRef.current || (document.querySelector(".chat-card") as HTMLElement | null);
      if (card) {
        const r = card.getBoundingClientRect();
        root.style.setProperty("--card-left",  Math.round(r.left + 10) + "px");
        root.style.setProperty("--card-width", Math.round(r.width - 20) + "px");
      }
    };
    const onFocusIn  = () => { setTimeout(update, 0); };
    const onFocusOut = () => { setTimeout(update, 0); };

    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    return () => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // Lock scroll to chat area
  useEffect(() => {
    const card = cardRef.current;
    const chat = chatRef.current;
    if (!card || !chat) return;
    const allowInsideChat = (e: Event) => { if (!chat.contains(e.target as Node)) e.preventDefault(); };
    const stopBubbling = (e: Event) => e.stopPropagation();

    card.addEventListener("touchmove", allowInsideChat, { passive: false, capture: true });
    card.addEventListener("wheel", allowInsideChat, { passive: false, capture: true });
    chat.addEventListener("touchmove", stopBubbling, { passive: false });
    chat.addEventListener("wheel", stopBubbling, { passive: false });
    return () => {
      card.removeEventListener("touchmove", allowInsideChat as any, { capture: true } as any);
      card.removeEventListener("wheel", allowInsideChat as any, { capture: true } as any);
      chat.removeEventListener("touchmove", stopBubbling as any);
      chat.removeEventListener("wheel", stopBubbling as any);
    };
  }, []);

  /* ---------- Back handler (safe on iOS) ---------- */
  const handleBack = useCallback(() => {
    try { textareaRef.current?.blur(); } catch {}
    abortRef.current?.abort(); // cancel any pending fetch
    setTimeout(() => {
      if (window.history.length > 1) window.history.back();
      else window.location.assign("/"); // fallback
    }, 0);
  }, []);

  /* ---------- Actions ---------- */
  async function sendMessage(text: string) {
    const trimmed = text.trim(); if (!trimmed || loading) return;
    setError(null);

    setMessages(m => [...m, { id: uid(), from: "user", text: trimmed, ts: Date.now() }]);
    scrollToBottom({ force: true });

    setInput("");
    if (!isIOS()) textareaRef.current?.focus({ preventScroll: true });

    setLoading(true);
    scrollToBottom({ force: true });

    // abort previous and create new controller
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: trimmed, sessionId: sid }),
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(await res.text());

      const data: any = await res.json().catch(() => ({}));
      const reply: string =
        data?.reply ?? data?.output ?? data?.answer ?? data?.text ??
        (typeof data === "string" ? data : JSON.stringify(data, null, 2)) ?? "…";

      if (!aliveRef.current) return; // page is leaving
      setMessages(m => [...m, { id: uid(), from: "bot", text: String(reply), ts: Date.now() }]);
      scrollToBottom({ force: true });
    } catch (e: any) {
      if (e?.name === "AbortError") return;  // Back pressed mid-request → ignore
      if (!aliveRef.current) return;
      setError("Network error. Please try again.");
      setMessages(m => [...m, { id: uid(), from: "system", text: "⚠️ Network error. Please try again.", ts: Date.now() }]);
      scrollToBottom({ force: true });
    } finally {
      if (!aliveRef.current) return;
      setLoading(false);
      if (!isIOS()) textareaRef.current?.focus({ preventScroll: true });
      saveSnapshot(); // ensure latest state is persisted
    }
  }

  function onSubmit(e: React.FormEvent) { e.preventDefault(); sendMessage(input); }
  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  }

  /* ---------- UI ---------- */
  const Bubble = ({ m }: { m: Msg }) => (
    <div className={`row ${m.from}`}>
      <div className="avatar" />
      <div className="bubble">
        <div className="text" dangerouslySetInnerHTML={{ __html: escapeHtml(m.text).replace(/\n/g, "<br/>") }} />
        {m.ts && <time className="time">{new Date(m.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>}
      </div>
    </div>
  );

  const showWelcomeOverlay = messages.length === 0 && input.trim().length === 0;

  return (
    <main className="chat-root">
      <div className="chat-card" ref={cardRef}>
        {/* Top bar with Back */}
        <div className="chat-topbar">
          <button className="back-btn" onClick={handleBack} aria-label="Back">← Back</button>
        </div>

        {/* Conversation (only scrollable area) */}
        <section ref={chatRef} className="chat-window" aria-live="polite">
          {showWelcomeOverlay && (
            <div className="welcome-overlay" aria-hidden>
              <div className="welcome-card">
                <h2>Welcome to Nextudio</h2>
                <p>How can we help you today?</p>
              </div>
            </div>
          )}

          {messages.map(m => <Bubble key={m.id} m={m} />)}
          {loading && (
            <div className="row bot">
              <div className="bubble typing"><span className="dot"/><span className="dot"/><span className="dot"/></div>
            </div>
          )}
          <div ref={endRef} />
        </section>

        {/* Input bar */}
        <form className="input-bar" onSubmit={onSubmit}>
          <textarea
            ref={textareaRef}
            className="input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask me anything..."
            rows={1}
            aria-label="Message"
          />
          <button
            className="send"
            type="submit"
            disabled={loading || !input.trim()}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
          >
            Send
          </button>
        </form>

        {error && <div className="error">{error}</div>}
      </div>
    </main>
  );
};

export default AssistantPage;
